import { DriveItem } from "../types";
import { FRIENDLY_TYPE_MAP, OFFICE_MIME_TYPES } from "../constants";

/** Absolute URL to a stored upload. */
export const buildUploadUrl = (path: string): string =>
  `${process.env.REACT_APP_API_BASE_URL}/Uploads/${path}`;

/** Google Docs viewer URL for an Office document. */
export const buildOfficeViewerUrl = (path: string): string =>
  `https://docs.google.com/gview?url=${encodeURIComponent(
    buildUploadUrl(path),
  )}&embedded=true`;

/** Icon file name (under assets/img/icons) for a drive item. */
export const getIconByMime = (item: DriveItem): string => {
  if (item.type === 1) return "folder-icon.svg"; // Folder
  if (!item.name) return "file.svg"; // fallback

  const extension = item.name?.split(".").pop()?.toLowerCase() || "";

  switch (extension) {
    case "pdf":
      return "pdf-icon.svg";
    case "doc":
    case "docx":
      return "file.svg";
    case "xls":
    case "xlsx":
    case "csv":
      return "xls-icon.svg";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return "image.svg";
    default:
      return "file.svg"; // fallback
  }
};

/** Human readable file size, e.g. "1.50 MB". */
export const formatSize = (bytes: number): string => {
  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024;
    i++;
  }
  return `${bytes.toFixed(2)} ${units[i]}`;
};

/** Friendly label for a mime type, e.g. "Excel", "PNG Image". */
export const getFriendlyFileType = (mimeType: string | null): string => {
  if (!mimeType) return "File";
  return (
    FRIENDLY_TYPE_MAP[mimeType] ||
    mimeType.split("/").pop()?.toUpperCase() ||
    "File"
  );
};

// --- Mime-type predicates -------------------------------------------------

export const isImageMime = (m?: string | null): boolean =>
  !!m?.startsWith("image/");
export const isVideoMime = (m?: string | null): boolean =>
  !!m?.startsWith("video/");
export const isAudioMime = (m?: string | null): boolean =>
  !!m?.startsWith("audio/");
export const isPdfMime = (m?: string | null): boolean =>
  m === "application/pdf";
export const isOfficeMime = (m?: string | null): boolean =>
  OFFICE_MIME_TYPES.includes(m || "");
export const isTextMime = (m?: string | null): boolean =>
  m === "application/json" || m === "text/csv" || !!m?.startsWith("text/");

/** Whether a file can be opened in the in-app preview modal. */
export const isPreviewable = (item: DriveItem): boolean => {
  if (item.type === 1) return false; // Folders are not previewable
  const m = item.mime_type;
  return (
    isImageMime(m) ||
    isPdfMime(m) ||
    isVideoMime(m) ||
    isAudioMime(m) ||
    isTextMime(m) ||
    isOfficeMime(m)
  );
};

export type PreviewKind =
  | "image"
  | "pdf"
  | "video"
  | "audio"
  | "text"
  | "office"
  | "icon";

/**
 * Resolve which preview element a file should use.
 * `previewText` controls whether text/json/csv files render inline
 * (only the thumbnail variant does; card/modal fall back to an icon).
 */
export const getPreviewKind = (
  item: DriveItem,
  opts?: { previewText?: boolean },
): PreviewKind => {
  const m = item.mime_type;
  if (isImageMime(m)) return "image";
  if (isPdfMime(m)) return "pdf";
  if (isVideoMime(m)) return "video";
  if (isAudioMime(m)) return "audio";
  if (opts?.previewText && isTextMime(m)) return "text";
  if (isOfficeMime(m)) return "office";
  return "icon";
};
